import type { ArcElement, Plugin } from 'chart.js';

/**
 * Pure percent calculation for a pie/doughnut slice, extracted so it is
 * testable without a canvas. Rounds to the nearest integer percent; returns 0
 * when the dataset total is zero or negative (guards div-by-zero and avoids
 * drawing garbage labels on an empty chart).
 */
export function percentFor(values: readonly number[], index: number): number {
  const total = values.reduce((sum, v) => sum + v, 0);
  if (total <= 0) {
    return 0;
  }
  return Math.round((values[index] / total) * 100);
}

// ---------------------------------------------------------------------------
// Pure label geometry/layout (spec §3: sample-style outside labels).
//
// Deliberately framework- and canvas-free: everything below is plain numeric
// geometry so it is fully unit-testable (positions, collision resolution,
// percent text) without a Chart.js instance or a 2D rendering context. The
// Chart.js plugin at the bottom is a thin adapter that reads real arc
// geometry (center/outerRadius/angles) off the chart's own elements, calls
// this function, and draws the result — it has no layout logic of its own.
// ---------------------------------------------------------------------------

export interface PieLabelSlice {
  readonly label: string;
  readonly percent: number;
  /**
   * The slice's own fill color. Used ONLY for the connecting leader line (spec §3
   * mandates the leader line, not the text, in the slice's color). Label TEXT always
   * draws in a fixed dark neutral — see `PIE_LABEL_TEXT_COLOR` — regardless of this
   * value, since text now sits on a white/card background where several palette
   * colors (e.g. `#ffb74d`, `#80cbc4`) fail WCAG AA contrast on their own.
   */
  readonly color: string;
  /** Midpoint angle of the slice's arc, in radians, canvas convention (0 = 3 o'clock, increases clockwise). */
  readonly midAngle: number;
}

export interface PieLabelGeometry {
  readonly cx: number;
  readonly cy: number;
  readonly outerRadius: number;
}

export interface PieLabelLayoutOptions {
  /** Distance from the arc's outer edge to the leader line's elbow, in px. Default 16. */
  readonly leaderLength?: number;
  /** Horizontal gap between the elbow and where the label text starts, in px. Default 4. */
  readonly textGap?: number;
  /** Minimum vertical distance enforced between two stacked labels on the same side, in px. Default 38 (clears a two-line label block with margin). */
  readonly minLineSpacing?: number;
  /**
   * Above this much vertical push, a label is considered "doesn't fit" (spec §3: "sığmazsa
   * en küçükler için yalnız leader'sız yakın konum" — if it doesn't fit, the smallest ones
   * just get a leaderless close position) and falls back to sitting close to its own slice
   * instead of joining the full-spacing stacked column. Default 60.
   */
  readonly maxPush?: number;
  /**
   * Minimum vertical distance enforced between a leaderless label and whatever was placed
   * right before it, in px. Smaller than `minLineSpacing` (leaderless labels are meant to
   * hug the pie tightly), but never 0 — several small adjacent slices can all miss the
   * `maxPush` cutoff in a row, and without their OWN spacing they'd stack on top of each
   * other instead of just skipping the full stack. Default 15.
   */
  readonly leaderlessSpacing?: number;
}

export interface PieLabelPlacement {
  readonly label: string;
  /** Pre-formatted `%NN` text (see `percentFor`). */
  readonly percentText: string;
  /** The slice's color — for the leader line only, never the label text. See `PieLabelSlice.color`. */
  readonly color: string;
  readonly side: 'left' | 'right';
  /** False once the stacked column has no room left for this slice — see `maxPush`. */
  readonly hasLeader: boolean;
  /** Point on the arc's own outer edge, where the leader line (if any) starts. */
  readonly anchor: { readonly x: number; readonly y: number };
  /** Where the leader line bends toward the text column (== anchor when `hasLeader` is false). */
  readonly elbow: { readonly x: number; readonly y: number };
  /** Text draw position (baseline vertically centered on this point). */
  readonly text: { readonly x: number; readonly y: number };
  readonly textAlign: 'left' | 'right';
}

const DEFAULT_LEADER_LENGTH = 16;
const DEFAULT_TEXT_GAP = 4;
const DEFAULT_MIN_LINE_SPACING = 38;
const DEFAULT_MAX_PUSH = 60;
const DEFAULT_LEADERLESS_SPACING = 15;

interface WorkingItem {
  readonly slice: PieLabelSlice;
  readonly side: 'left' | 'right';
  readonly cos: number;
  readonly anchor: { x: number; y: number };
  /** Unpushed elbow y — where this label would sit with no other labels around. */
  readonly naturalY: number;
  y: number;
  hasLeader: boolean;
}

/**
 * Resolves final label positions for a full ring of pie slices: every slice gets a
 * placement (spec §3 — no minimum-percent cutoff anymore, even a 0-1% sliver is
 * labeled), split into a left and a right stack (by which half of the circle its
 * anchor falls in), each independently decluttered by a simple top-to-bottom sweep —
 * sort by natural vertical position, then push any label down that would otherwise
 * overlap the one above it by less than `minLineSpacing`. A label pushed further than
 * `maxPush` from its own natural position is judged not to fit the full-spacing stack
 * and instead falls back to a "close" position, hugging its own slice — but it is
 * still only allowed to sit as close as `leaderlessSpacing` to whatever was placed
 * immediately before it. Without that second, smaller spacing floor, several small
 * adjacent slices that ALL miss the `maxPush` cutoff would each independently snap
 * back to their own (mutually close) anchor points and overlap each other — measured
 * against a real 14-category dataset (AKASYA visitShares.mall) during dev smoke, three
 * adjacent small categories piled into a ~10px band before this floor was added. The
 * result is a single monotonic sweep down the column: every label — leadered or not —
 * ends up strictly farther down than the one above it, by at least one of the two
 * spacings.
 *
 * Returned in the SAME order as `slices` (1:1 by index) — callers zip this back
 * against their own dataset by index, so the internal per-side sort must never leak
 * into the output ordering.
 */
export function layoutPieLabels(
  slices: readonly PieLabelSlice[],
  geometry: PieLabelGeometry,
  options: PieLabelLayoutOptions = {},
): PieLabelPlacement[] {
  const leaderLength = options.leaderLength ?? DEFAULT_LEADER_LENGTH;
  const textGap = options.textGap ?? DEFAULT_TEXT_GAP;
  const minLineSpacing = options.minLineSpacing ?? DEFAULT_MIN_LINE_SPACING;
  const maxPush = options.maxPush ?? DEFAULT_MAX_PUSH;
  const leaderlessSpacing = options.leaderlessSpacing ?? DEFAULT_LEADERLESS_SPACING;
  const { cx, cy, outerRadius } = geometry;

  const items: WorkingItem[] = slices.map((slice) => {
    const cos = Math.cos(slice.midAngle);
    const sin = Math.sin(slice.midAngle);
    const anchor = { x: cx + outerRadius * cos, y: cy + outerRadius * sin };
    const naturalY = cy + (outerRadius + leaderLength) * sin;
    return { slice, side: cos < 0 ? 'left' : 'right', cos, anchor, naturalY, y: naturalY, hasLeader: true };
  });

  for (const side of ['left', 'right'] as const) {
    const column = items.filter((item) => item.side === side).sort((a, b) => a.naturalY - b.naturalY);
    let prevY = -Infinity;
    for (const item of column) {
      const candidateY = Math.max(item.naturalY, prevY + minLineSpacing);
      if (candidateY - item.naturalY > maxPush) {
        // Doesn't fit the full-spacing stack — drop it close to its own slice instead
        // of pushing it arbitrarily far away, but still enforce the smaller leaderless
        // floor against whatever came before it, so a run of small adjacent slices
        // can't all collapse onto (near-)identical positions.
        const closeY = Math.max(item.anchor.y, prevY + leaderlessSpacing);
        item.hasLeader = false;
        item.y = closeY;
        prevY = closeY;
        continue;
      }
      item.y = candidateY;
      prevY = candidateY;
    }
  }

  return items.map((item) => {
    const textAlign: 'left' | 'right' = item.side === 'right' ? 'left' : 'right';
    // Leaderless labels stay horizontally at the anchor (hug the slice edge); only
    // their y may have moved, per the leaderlessSpacing floor above.
    const elbow = item.hasLeader
      ? { x: cx + (outerRadius + leaderLength) * item.cos, y: item.y }
      : { x: item.anchor.x, y: item.y };
    const textX = item.side === 'right' ? elbow.x + textGap : elbow.x - textGap;
    return {
      label: item.slice.label,
      percentText: `%${item.slice.percent}`,
      color: item.slice.color,
      side: item.side,
      hasLeader: item.hasLeader,
      anchor: item.anchor,
      elbow,
      text: { x: textX, y: elbow.y },
      textAlign,
    };
  });
}

const PIE_LABEL_FONT = '600 11px sans-serif';
const PIE_PERCENT_FONT = '400 10px sans-serif';
const PIE_LABEL_LINE_GAP = 12;

/**
 * Fixed, high-contrast label TEXT color. Restored from the previous inside-the-slice
 * design's own measured choice (that code's deleted comment documented contrast
 * ratios against every PALETTE color across report-floors/report-categories/
 * report-frequency — several light tones, e.g. `#ffb74d` (~1.73:1), `#80cbc4`
 * (~1.87:1), fail WCAG AA well before reaching black's ~3.9:1+ minimum). Labels moved
 * outside the slice onto a white/card background, but that does NOT fix the problem:
 * drawing the text itself in the slice's own (often light) color still fails AA
 * against white for the same colors. Spec §3 only asks for the LEADER LINE in the
 * slice's color — the label text always uses this fixed dark neutral instead,
 * regardless of which slice it belongs to.
 */
export const PIE_LABEL_TEXT_COLOR = '#000000';

/**
 * Chart.js inline plugin drawing sample-style OUTSIDE labels for each pie/doughnut
 * slice: `{category name}` above `%NN` below, always in `PIE_LABEL_TEXT_COLOR`,
 * joined to the arc by a short bent leader line (radial segment out to an elbow,
 * then a short horizontal segment to the text) drawn in the SLICE'S OWN color (spec
 * §3 — only the leader is slice-colored, not the text; see `PIE_LABEL_TEXT_COLOR`'s
 * doc comment for why). No new npm dependency (chartjs-plugin-datalabels is not
 * used) — wired per-chart via `p-chart [plugins]`.
 *
 * All layout math (positions, per-side collision avoidance, the leaderless fallback)
 * lives in `layoutPieLabels` above and is unit-tested there; this function only reads
 * real geometry off Chart.js's own `ArcElement`s (`x`/`y`/`startAngle`/`endAngle`/
 * `outerRadius` — all real, statically-typed fields, see `element.arc.d.ts`) and draws
 * the result. No `MIN_LABEL_PERCENT` cutoff anymore — spec §3: every slice is labeled,
 * even one that rounds to 0-1%.
 */
export interface PiePercentLabelsOptions {
  /**
   * `false` when the chart shows a Chart.js legend that already lists the slice names —
   * the outside label then draws the percentage alone instead of repeating the name.
   * Defaults to `true` (name above, percent below), the original sample-style label.
   */
  readonly showSliceLabels?: boolean;
}

/** Per-chart options, read from `options.plugins.piePercentLabels` (Chart.js's own convention). */
function readOptions(chart: { options?: { plugins?: unknown } }): PiePercentLabelsOptions {
  const plugins = chart.options?.plugins as Record<string, unknown> | undefined;
  return (plugins?.['piePercentLabels'] as PiePercentLabelsOptions | undefined) ?? {};
}

export const piePercentLabels: Plugin<'pie'> = {
  id: 'piePercentLabels',
  afterDatasetsDraw(chart) {
    const dataset = chart.data.datasets[0];
    if (!dataset) {
      return;
    }
    const values = dataset.data as number[];
    const total = values.reduce((sum, v) => sum + v, 0);
    if (total <= 0) {
      return;
    }

    const labels = (chart.data.labels ?? []) as string[];
    // Chart.js allows `backgroundColor` to be a single scalar Color instead of a
    // per-point array (uniform fill for every slice) — indexing a bare string with
    // `[index]` silently returns individual CHARACTERS (e.g. '#'), not colors, so this
    // must be guarded rather than blindly cast. Falls back to the same fixed dark
    // neutral used for text when it isn't an array (no legitimate per-slice color to
    // read), rather than drawing a leader in a bogus color.
    const rawBackgroundColor: unknown = dataset.backgroundColor;
    const colors = Array.isArray(rawBackgroundColor) ? (rawBackgroundColor as string[]) : undefined;
    const arcs = chart.getDatasetMeta(0).data as ArcElement[];
    const first = arcs[0];
    if (!first) {
      return;
    }

    const slices: PieLabelSlice[] = arcs.map((arc, index) => ({
      label: labels[index] ?? '',
      percent: percentFor(values, index),
      color: colors?.[index] ?? PIE_LABEL_TEXT_COLOR,
      midAngle: (arc.startAngle + arc.endAngle) / 2,
    }));

    const placements = layoutPieLabels(slices, { cx: first.x, cy: first.y, outerRadius: first.outerRadius });

    const { showSliceLabels = true } = readOptions(chart);
    const { ctx } = chart;
    ctx.save();
    ctx.lineWidth = 1;
    ctx.textBaseline = 'middle';

    for (const placement of placements) {
      ctx.strokeStyle = placement.color; // leader line: slice-colored (spec §3).
      ctx.fillStyle = PIE_LABEL_TEXT_COLOR; // label text: always the fixed dark neutral (WCAG AA).
      ctx.textAlign = placement.textAlign;

      if (placement.hasLeader) {
        ctx.beginPath();
        ctx.moveTo(placement.anchor.x, placement.anchor.y);
        ctx.lineTo(placement.elbow.x, placement.elbow.y);
        const lineEndX = placement.side === 'right' ? placement.text.x - 2 : placement.text.x + 2;
        ctx.lineTo(lineEndX, placement.elbow.y);
        ctx.stroke();
      }

      if (showSliceLabels) {
        ctx.font = PIE_LABEL_FONT;
        ctx.fillText(placement.label, placement.text.x, placement.text.y - PIE_LABEL_LINE_GAP / 2);
        ctx.font = PIE_PERCENT_FONT;
        ctx.fillText(placement.percentText, placement.text.x, placement.text.y + PIE_LABEL_LINE_GAP / 2);
      } else {
        ctx.font = PIE_PERCENT_FONT;
        ctx.fillText(placement.percentText, placement.text.x, placement.text.y);
      }
    }

    ctx.restore();
  },
};
